// 언어별 네이티브 UI 카피 (디자인 핸드오프 확정본). 번역 아님.
// 생성 카피(헤드라인·스탯 코멘트)는 judge.ts. 유형명·컬러는 archetypeMeta.ts.

import type { Lang } from "../lib/types";
import { ARCHETYPE_META } from "../lib/archetypeMeta";

export interface Strings {
  tagline: string; // 스플래시(옵션)
  label: string; // 노트 상단 "받는 사람 없음 —"
  placeholder: string; // 2줄(\n 포함)
  micHint: string; // "눌러서 말하기"
  listening: string; // "듣는 중"
  transcribing: string; // "받아쓰는 중…"
  getApp: string; // 웹 → 스토어 설치 유도 FAB
  hyuhResult: string; // hyuh 크로스프로모(결과 상세 하단)
  hyuhSettings: string; // hyuh 크로스프로모(설정)
  submit: string; // "구겨서 버리기"
  keepGoing: string; // 제출 비활성 시
  reassure: string; // 안심 한 줄
  // 결과
  kicker: string; // "이번 분노 유형"
  aiLine: string; // "AI 한 줄" 라벨
  aiStat: string; // "AI 코멘트" 라벨
  loadingLines: string[]; // 가짜 과학 로딩 풀(랜덤 회전)
  detailButton: string; // "정밀 분석 보기"
  lockMore: string; // "더 있음"
  flexit: string; // "자랑하기"
  writeAgain: string; // "다시 쓰기"
  close: string; // 헤더 ✕
  back: string; // 헤더 ←
  // 스탯 라벨(고정)
  stSpeed: string;
  stBack: string;
  stTime: string;
  stFirst: string;
  stVolume: string;
  angerCurve: string;
  peak: string;
  // 공유 카드
  cardLabel: string; // 헤더 우측 "공유 카드 · 9:16"
  metaA: string; // "오늘 지구에서"
  metaB: string; // "번째로 빡친 사람"
  brand: string; // "bwek에서 채점"
  save: string;
  share: string;
  shareText: string; // 공유 시 첨부 캡션(언어별)
  // 설정
  setTitle: string;
  setLang: string;
  setSound: string;
  setTerms: string;
  setPrivacy: string;
  setNoAcct: string;
  // off_scope (위로 단어 0)
  osStep: string; // 2줄
  osThrow: string; // "그래도, 버리는 건 해줄게요."
  // crisis (민감 · 농담 0)
  crStop: string; // 2줄
  crMore: string; // "다른 창구"
  crNote: string; // "24시간 · 무료"
}

export const STRINGS: Record<Lang, Strings> = {
  ko: {
    tagline: "쓰고, 버린다",
    label: "받는 사람 없음 —",
    placeholder: "빡친 일을\n적어보자.",
    micHint: "눌러서 말하기",
    listening: "듣는 중",
    transcribing: "받아쓰는 중…",
    getApp: "앱으로 받기",
    hyuhResult: "위로는 없었죠. hyuh엔 있어요.",
    hyuhSettings: "hyuh: 위로받아도 되는 밤에.",
    submit: "구겨서 버리기",
    keepGoing: "더 써봐요",
    reassure: "저장 안 함. 학습 안 함. 바로 버림.",
    kicker: "이번 분노 유형",
    aiLine: "AI 한 줄",
    aiStat: "AI 코멘트",
    loadingLines: [
      "타건 압력 측정 중",
      "분노 입자 포집 중",
      "백스페이스 빈도 분석 중",
      "빡침 곡선 작도 중",
      "타속 보정 중",
      "어이없음 농도 측정 중",
      "분노 표본 원심분리 중",
      "데시벨 환산 중",
    ],
    detailButton: "정밀 분석 보기",
    lockMore: "더 있음",
    flexit: "자랑하기",
    writeAgain: "다시 쓰기",
    close: "닫기",
    back: "뒤로",
    stSpeed: "타속",
    stBack: "백스페이스",
    stTime: "작성 시간",
    stFirst: "시작까지",
    stVolume: "음량",
    angerCurve: "빡침 곡선",
    peak: "빡침 피크",
    cardLabel: "공유 카드 · 9:16",
    metaA: "오늘 지구에서",
    metaB: "번째로 빡친 사람",
    brand: "bwek에서 채점",
    save: "저장",
    share: "공유",
    shareText: "나 이렇게 채점당함. 너는 몇 점? bwek.app",
    setTitle: "설정",
    setLang: "언어",
    setSound: "소리",
    setTerms: "이용약관",
    setPrivacy: "개인정보 처리방침",
    setNoAcct: "계정 없음",
    osStep: "이건 빡침이 아니네요.\n채점은 안 할게요.",
    osThrow: "그래도, 버리는 건 해줄게요.",
    crStop: "잠깐 멈출게요.\n지금 많이 힘들면, 아래로 연락해요.",
    crMore: "다른 창구",
    crNote: "24시간 · 무료",
  },
  en: {
    tagline: "write it. throw it away",
    label: "dear nobody —",
    placeholder: "drop the\nrage here.",
    micHint: "tap to talk",
    listening: "listening",
    transcribing: "transcribing…",
    getApp: "Get the app",
    hyuhResult: "no comfort here. hyuh has it.",
    hyuhSettings: "hyuh: nights you're allowed comfort.",
    submit: "crumple it",
    keepGoing: "keep going",
    reassure: "not saved. not used for training. trashed on the spot.",
    kicker: "today's rage type",
    aiLine: "ai verdict",
    aiStat: "ai note",
    loadingLines: [
      "measuring keystroke force",
      "collecting rage particles",
      "analyzing backspace frequency",
      "plotting the anger curve",
      "calibrating typing speed",
      "isolating the sample",
      "centrifuging the rage",
      "converting to decibels",
    ],
    detailButton: "see the full autopsy",
    lockMore: "there's more",
    flexit: "flex it",
    writeAgain: "write again",
    close: "close",
    back: "back",
    stSpeed: "typing speed",
    stBack: "backspaces",
    stTime: "time spent",
    stFirst: "time to first word",
    stVolume: "volume",
    angerCurve: "rage curve",
    peak: "peak rage",
    cardLabel: "your card · 9:16",
    metaA: "the",
    metaB: "th angriest on earth today",
    brand: "scored on bwek",
    save: "save",
    share: "share",
    shareText: "Got judged. What's your score? bwek.app",
    setTitle: "settings",
    setLang: "language",
    setSound: "sound",
    setTerms: "terms",
    setPrivacy: "privacy",
    setNoAcct: "no account",
    osStep: "this one isn't rage.\nwe won't score it.",
    osThrow: "still going to throw it out, though.",
    crStop: "stopping here for a moment.\nif it's a lot right now, reach out below.",
    crMore: "more options",
    crNote: "24/7 · free",
  },
  ja: {
    tagline: "書いて、捨てる",
    label: "宛先なし —",
    placeholder: "イラついたこと、\n書いとこ。",
    micHint: "押して話す",
    listening: "聞いてる",
    transcribing: "書き起こし中…",
    getApp: "アプリで使う",
    hyuhResult: "ここでは慰めない。hyuhにはある。",
    hyuhSettings: "hyuh：慰められてもいい夜に。",
    submit: "丸めて捨てる",
    keepGoing: "もっと書いて",
    reassure: "保存しない。学習に使わない。すぐ捨てる。",
    kicker: "今回の怒りタイプ",
    aiLine: "AI 一言",
    aiStat: "AI コメント",
    loadingLines: [
      "打鍵圧を計測中",
      "怒り粒子を採取中",
      "バックスペース頻度を解析中",
      "イライラ曲線を作図中",
      "打鍵速度を補正中",
      "理不尽濃度を測定中",
      "サンプルを遠心分離中",
      "デシベル換算中",
    ],
    detailButton: "精密分析を見る",
    lockMore: "まだあります",
    flexit: "自慢する",
    writeAgain: "もう一度書く",
    close: "閉じる",
    back: "戻る",
    stSpeed: "打鍵速度",
    stBack: "バックスペース",
    stTime: "所要時間",
    stFirst: "書き出しまで",
    stVolume: "音量",
    angerCurve: "イライラ曲線",
    peak: "怒りピーク",
    cardLabel: "シェアカード · 9:16",
    metaA: "今日、地球で",
    metaB: "番目にブチギレた人",
    brand: "bwekで採点",
    save: "保存",
    share: "シェア",
    shareText: "採点された。君は何点？ bwek.app",
    setTitle: "設定",
    setLang: "言語",
    setSound: "サウンド",
    setTerms: "利用規約",
    setPrivacy: "プライバシーポリシー",
    setNoAcct: "アカウントなし",
    osStep: "これはイライラじゃないですね。\n採点はしません。",
    osThrow: "それでも、捨てるのはやっておきます。",
    crStop: "ここで一度、止めます。\n今つらいときは、下の窓口へ。",
    crMore: "他の窓口",
    crNote: "24時間 · 無料",
  },
};

/** 유형명(언어별 독립 작명). archetypeMeta 단일 출처. */
export const ARCHETYPE_NAME: Record<string, Record<Lang, string>> = Object.fromEntries(
  Object.entries(ARCHETYPE_META).map(([id, m]) => [id, m.name]),
);

/** 유형 노출 형식 구분자 (카피 납품본): KR 공백, EN ": ", JP 전각 "：". */
export function kickerSep(lang: Lang): string {
  return lang === "en" ? ": " : lang === "ja" ? "：" : " ";
}

/** 로케일 날짜 표기(공유 카드 헤더 "BWEK · {날짜}"). */
export function locDate(lang: Lang, d: Date = new Date()): string {
  if (lang === "ja") return `${d.getMonth() + 1}月${d.getDate()}日`;
  if (lang === "ko") return `${d.getMonth() + 1}월 ${d.getDate()}일`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
}
